import { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { api, adminGetUsers, adminChangePassword, adminDeleteUser } from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Building2, FileText, DollarSign, Search, Key, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const USERS_PER_PAGE = 20;

export default function AdminDashboard() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Password change modal
  const [passwordModal, setPasswordModal] = useState({ open: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Filter users when search query changes and reset to page 1
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user => 
        (user.email || '').toLowerCase().includes(query) ||
        (user.name || '').toLowerCase().includes(query) ||
        (user.surname || '').toLowerCase().includes(query) ||
        (user.companyName || '').toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
    setCurrentPage(1); // Reset to first page on search
  }, [searchQuery, users]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const endIndex = startIndex + USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const loadDashboardData = async () => {
    try {
      const [statsRes, usersRes, requestsRes, transactionsRes] = await Promise.all([
        api.get('/admin/stats'),
        adminGetUsers(),
        api.get('/admin/requests?limit=50'),
        api.get('/admin/transactions?limit=50')
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data || []);
      setFilteredUsers(usersRes.data || []);
      setRequests(requestsRes.data || []);
      setTransactions(transactionsRes.data || []);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      toast.error('Errore caricamento dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente? Questa azione è irreversibile.')) return;

    try {
      await adminDeleteUser(userId);
      toast.success('Utente eliminato');
      loadDashboardData();
    } catch (err) {
      toast.error('Errore durante eliminazione');
    }
  };

  const openPasswordModal = (user) => {
    setPasswordModal({ open: true, user });
    setNewPassword('');
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setChangingPassword(true);
    try {
      await adminChangePassword(passwordModal.user.id, newPassword);
      toast.success(`Password aggiornata per ${passwordModal.user.email}`);
      setPasswordModal({ open: false, user: null });
      setNewPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante il cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="text-center py-12">Caricamento...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Toaster />
      <div>
        <h1 className="text-3xl font-bold mb-6">Dashboard Amministrativa</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Clienti Totali</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalClients || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aziende Totali</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalCompanies || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Richieste Totali</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalRequests || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue Totale</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">EUR {stats?.totalRevenue?.toFixed(2) || '0.00'}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users">{t('ui.users')} ({users.length})</TabsTrigger>
            <TabsTrigger value="requests">{t('ui.requests')}</TabsTrigger>
            <TabsTrigger value="transactions">{t('ui.transactions')}</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('ui.userManagement')}</CardTitle>
                  <div className="text-sm text-slate-500">
                    {filteredUsers.length} utenti trovati
                    {totalPages > 1 && ` • Pagina ${currentPage} di ${totalPages}`}
                  </div>
                </div>
                {/* Search Bar */}
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Cerca per email o nome..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="admin-user-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {/* User list */}
                <div className="space-y-3">
                  {paginatedUsers.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      {searchQuery ? 'Nessun utente trovato' : 'Nessun utente registrato'}
                    </div>
                  ) : (
                    paginatedUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name || user.companyName || 'N/A'}</p>
                          <p className="text-sm text-slate-500 truncate">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              user.role === 'client' ? 'bg-blue-100 text-blue-700' : 
                              user.role === 'company' ? 'bg-purple-100 text-purple-700' : 
                              'bg-red-100 text-red-700'
                            }`}>
                              {user.role === 'client' ? '👤 Cliente' : 
                               user.role === 'company' ? '🏢 Azienda' : 
                               '👑 Admin'}
                            </span>
                            {user.plan?.status && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                user.plan.status === 'active' ? 'bg-green-100 text-green-700' : 
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {user.plan.status}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPasswordModal(user)}
                            title="Cambia password"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Elimina utente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm text-slate-500">
                      Mostrando {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} di {filteredUsers.length}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        data-testid="admin-pagination-prev"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Precedente
                      </Button>
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className="w-8 h-8 p-0"
                              data-testid={`admin-pagination-page-${pageNum}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="admin-pagination-next"
                      >
                        Successivo
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('ui.recentRequests')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-y-auto space-y-3 pr-2">
                  {requests.map((req) => (
                    <div key={req.id} className="p-3 border rounded-lg">
                      <p className="font-medium">{req.title}</p>
                      <p className="text-sm text-slate-600">{req.description?.substring(0, 100)}...</p>
                      <div className="flex gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          req.status === 'open' ? 'bg-green-100 text-green-700' :
                          req.status === 'closed' ? 'bg-slate-100 text-slate-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{req.status}</span>
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{req.offersCount || 0} offerte</span>
                        <span className="text-xs px-2 py-1 bg-slate-100 rounded">{req.district || req.region}</span>
                        {(req.contactedBy?.length || 0) > 0 && (
                          <button 
                            onClick={async () => {
                              try {
                                await api.post('/admin/requests/' + req.id + '/reset-contacts');
                                toast.success('Contatti resettati');
                                loadDashboardData();
                              } catch (err) { toast.error('Errore reset contatti'); }
                            }}
                            className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                            data-testid={`reset-contacts-${req.id}`}
                          >
                            Sblocca ({req.contactedBy?.length || 0} contatti)
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('ui.recentTransactions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-y-auto space-y-3 pr-2">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{tx.package_id === 'subscription' ? 'Abbonamento' : 'Sblocco Chat'}</p>
                        <p className="text-sm text-slate-500">{tx.user_email}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(tx.createdAt).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">EUR {tx.amount}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {tx.payment_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Password Change Modal */}
      <Dialog open={passwordModal.open} onOpenChange={(open) => !open && setPasswordModal({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ui.changePassword')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Stai modificando la password per: <strong>{passwordModal.user?.email}</strong>
            </p>
            <Input
              type="password"
              placeholder="Nuova password (min. 6 caratteri)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordModal({ open: false, user: null })}>
              Annulla
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
